import { cva, type VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';
import { Label, Radio, RadioGroup, RadioGroupProps, RadioProps } from 'react-aria-components';
import { cn } from './utils';

const agRadioCircleVariants = cva(
  'rounded-full border bg-white shadow-xs flex shrink-0 items-center justify-center transition-colors',
  {
    variants: {
      size: { sm: 'w-4 h-4', md: 'w-4.5 h-4.5', lg: 'w-5 h-5' },
      variant: { default: 'border-neutral-5 group-data-selected:border-brand-6', error: 'border-red-text' },
      isDisabled: { true: 'opacity-50 cursor-not-allowed', false: '' },
    },
    defaultVariants: { size: 'md', variant: 'default', isDisabled: false },
  }
);

const agRadioDotVariants = cva('rounded-full bg-brand-6 opacity-0 group-data-selected:opacity-100 transition-opacity', {
  variants: { size: { sm: 'w-2 h-2', md: 'w-2.5 h-2.5', lg: 'w-3 h-3' } },
  defaultVariants: { size: 'md' },
});

const agLabelVariants = cva('text-neutral-text', {
  variants: { size: { sm: 'text-13', md: 'text-14', lg: 'text-16' } },
  defaultVariants: { size: 'md' },
});

type AgRadioVariantProps = VariantProps<typeof agRadioCircleVariants>;

export interface AgRadioProps extends Omit<RadioProps, 'children'> {
  /** Label text displayed next to the radio */
  label?: ReactNode;
  /** Size variant */
  size?: AgRadioVariantProps['size'];
  /** Whether the radio is in an error state */
  isInvalid?: boolean;
  /** Additional class for the container */
  className?: string;
}

export function AgRadio({ label, size = 'md', isInvalid, isDisabled, className, ...props }: AgRadioProps) {
  return (
    <Radio
      isDisabled={isDisabled}
      className={cn('group flex items-center gap-2 cursor-pointer', isDisabled && 'cursor-not-allowed', className)}
      {...props}
    >
      <div className={cn(agRadioCircleVariants({ size, variant: isInvalid ? 'error' : 'default', isDisabled }))}>
        <div className={cn(agRadioDotVariants({ size }))} />
      </div>
      {label && <div className={cn(agLabelVariants({ size }))}>{label}</div>}
    </Radio>
  );
}

AgRadio.displayName = 'AgRadio';

export interface AgRadioGroupProps extends Omit<RadioGroupProps, 'children'> {
  /** Label text displayed above the radio group */
  label?: string;
  /** The radio items */
  children: ReactNode;
  /** Additional class for the container */
  className?: string;
}

export function AgRadioGroup({ label, children, className, ...props }: AgRadioGroupProps) {
  return (
    <RadioGroup className={cn('flex flex-col gap-4', className)} {...props}>
      {label && <Label className="text-14 font-medium text-neutral-text">{label}</Label>}
      <div className="flex flex-col gap-2">{children}</div>
    </RadioGroup>
  );
}

AgRadioGroup.displayName = 'AgRadioGroup';
